import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import * as AuthApi from "@/api/auth";
import type { Me } from "@/api/auth";
import { wsManager } from "@/api/ws";

export type { Me };

interface LegacyUser {
  id: string;
  email: string;
  user_metadata?: Record<string, unknown>;
  app_metadata?: Record<string, unknown>;
}

interface AuthCtx {
  me: Me | null;
  user: LegacyUser | null;
  loading: boolean;
  signIn: (input: {
    tenant_slug: string;
    email: string;
    password: string;
  }) => Promise<void>;
  signUp: (input: {
    tenant_slug: string;
    tenant_name: string;
    email: string;
    password: string;
  }) => Promise<void>;
  signOut: () => Promise<void>;
  refresh: () => Promise<void>;
}

const Ctx = createContext<AuthCtx | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<Me | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setMe(await AuthApi.fetchMe());
  }, []);

  useEffect(() => {
    refresh().finally(() => setLoading(false));
    const onUnauth = () => setMe(null);
    window.addEventListener("auth.unauthorized", onUnauth);
    return () => window.removeEventListener("auth.unauthorized", onUnauth);
  }, [refresh]);

  // Bridge me <-> WebSocket lifetime. The access_token cookie is sent on the
  // handshake automatically (Domain=.hermys.ai). Backend accepts cookie OR
  // ?token= since the Phase 3 retrofit (Chunk 0).
  useEffect(() => {
    if (!me) {
      wsManager.disconnect();
      return;
    }
    wsManager.connect("/ws/v1/sales");
    return () => wsManager.disconnect();
  }, [me]);

  const signIn = useCallback<AuthCtx["signIn"]>(async (input) => {
    const user = await AuthApi.login(input);
    setMe(user);
  }, []);

  const signUp = useCallback<AuthCtx["signUp"]>(async (input) => {
    await AuthApi.register(input);
    // Registration doesn't issue a session; the screen calls signIn next.
  }, []);

  const signOut = useCallback<AuthCtx["signOut"]>(async () => {
    await AuthApi.logout();
    setMe(null);
  }, []);

  const user = useMemo<LegacyUser | null>(() => {
    if (!me) return null;
    return { id: me.id, email: me.email };
  }, [me]);

  const value = useMemo(
    () => ({ me, user, loading, signIn, signUp, signOut, refresh }),
    [me, user, loading, signIn, signUp, signOut, refresh],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthCtx {
  const v = useContext(Ctx);
  if (!v) throw new Error("useAuth must be used inside <AuthProvider>");
  return v;
}
